import { isParthaForm, ParthaConsentDocument } from './ParthaConsentDocument';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  fetchCaseSheetConsents,
  type ConsentTemplateWireView,
  type TreatmentConsentView,
} from '@/dashboard/api';
import PrintSheet from '@/dashboard/ui/PrintSheet';
import { AcButton, ErrorState, LoadingState } from '@/dashboard/ui';
import { formatTimestampIst } from '@/dashboard/owner/Lead360Panel';
import { consultErrorMessage, parseConsultAppointmentId } from '@/dashboard/doctor/doctor-consult-view';
import { consultPathForAppointment } from '@/dashboard/doctor/doctor-home-view';

/**
 * DOCTOR-OS-EXPERIENCE-V1 (B3) -- the treatment-consent PRINT page
 * (/dashboard/patients/treatment-consent-print?caseSheetId=..&consentId=..).
 *
 * PAPER INFORMATION SET (PARITY LAW): the printed form carries the paper
 * consent's exact field set -- CONSENT FORM + consent number | date | MR No
 * | Name | Age/Sex | Address | Mobile | Reference | Doctor -- then the
 * tenant-approved authorisation wording VERBATIM (LEGAL TEXT LAW: the ONLY
 * place the legal text renders) + the named treatment lines + the ruled
 * signature block. Printing rides THE registered PrintSheet primitive
 * (window.print / browser print-to-PDF -- the house print rail).
 *
 * DATA + PHI BOUNDS: the consent and referenced wording load through the
 * doctor-gated GET /api/case-sheets/:id/consents. Generic legacy print keeps
 * its existing opener identity fields. Exact supplied Partha forms instead
 * use the owning patient/clinic/visit identity saved by the database in the
 * immutable wording snapshot; URL labels never override that identity.
 * Contact digits and signature bytes remain absent from this read contract.
 *
 * SIGNED-DIGITAL RENDER: signature BYTES are structurally absent from the
 * consent wire (domain law -- bytes never ride a list read), so a
 * signed_digital consent renders the SIGNED FACT (who + when, in the
 * signature slot) instead of a blank rule; it never fabricates a bitmap.
 */

export const TREATMENT_CONSENT_PRINT_TITLE = 'CONSENT FORM';
export const TREATMENT_CONSENT_PRINT_BACK = 'Back to consult';
const DASH = '-';

interface PrintIdentity {
  readonly umr: string | null;
  readonly name: string | null;
  readonly age: string | null;
  readonly sex: string | null;
  readonly treatments: readonly string[];
}

export function TreatmentConsentPrintContent({
  consent,
  template,
  identity,
}: {
  readonly consent: TreatmentConsentView;
  readonly template: ConsentTemplateWireView | null;
  readonly identity: PrintIdentity;
}): JSX.Element {
  if (template && isParthaForm(template)) return <ParthaConsentDocument template={template} consent={consent}/>;
  const treatments =
    identity.treatments.length > 0
      ? identity.treatments
      : consent.treatmentPlanItemIds.map((id) => `Plan line #${id}`);
  const ageSex = [identity.age, identity.sex]
    .filter((v): v is string => v !== null && v !== '')
    .join(' / ');
  const headerFields: ReadonlyArray<{ label: string; value: string }> = [
    {
      label: 'Date',
      value: formatTimestampIst(consent.signedAt ?? consent.createdAt),
    },
    { label: 'MR No', value: identity.umr ?? DASH },
    { label: 'Name', value: identity.name ?? DASH },
    { label: 'Age/Sex', value: ageSex !== '' ? ageSex : DASH },
    { label: 'Address', value: DASH },
    { label: 'Mobile', value: DASH },
    { label: 'Reference', value: DASH },
    { label: 'Doctor Name', value: consent.doctorName ?? DASH },
  ];
  return (
    <div className="tc-print" data-testid="treatment-consent-print-sheet">
      {template?.brandLine ? (
        <p className="tc-print__brand">{template.brandLine}</p>
      ) : null}
      {template?.jurisdictionCity ? (
        <p className="tc-print__city">{template.jurisdictionCity}</p>
      ) : null}
      <div className="tc-print__headline">
        <h2 className="tc-print__form-title">{TREATMENT_CONSENT_PRINT_TITLE}</h2>
        <span className="tc-print__number" data-testid="tc-print-number">
          {consent.consentNumber}
        </span>
      </div>
      <dl className="tc-print__fields">
        {headerFields.map((f) => (
          <div className="tc-print__field" key={f.label}>
            <dt>{f.label}</dt>
            <dd>{f.value}</dd>
          </div>
        ))}
      </dl>
      {template !== null ? (
        <div className="tc-print__body">
          <h3 className="tc-print__auth-title">{template.title}</h3>
          {/* LEGAL TEXT LAW: the tenant-approved wording VERBATIM -- never
              rewritten, reflowed only by white-space: pre-wrap. */}
          <p className="tc-print__auth-text" data-testid="tc-print-authorisation">
            {template.authorisationText}
          </p>
        </div>
      ) : (
        <p className="tc-print__body" data-testid="tc-print-no-template">
          No approved consent wording is configured for this organization.
          Nothing is invented in its place.
        </p>
      )}
      <div className="tc-print__treatments">
        <h4>Treatment</h4>
        <ul data-testid="tc-print-treatments">
          {treatments.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>
      </div>
      <dl className="tc-print__signatures">
        <div className="tc-print__sig">
          <dt>Signature of the Patient / Guardian</dt>
          {consent.state === 'signed_digital' ? (
            <dd className="tc-print__signed" data-testid="tc-print-signed-digital">
              Signed digitally in AdoraCare
              {consent.signerName !== null ? ` - ${consent.signerName}` : ''}
              {consent.signedAt !== null
                ? ` - ${formatTimestampIst(consent.signedAt)}`
                : ''}
            </dd>
          ) : (
            <dd className="tc-print__rule" aria-hidden="true" />
          )}
        </div>
        <div className="tc-print__sig">
          <dt>Date</dt>
          {consent.signedAt !== null ? (
            <dd>{formatTimestampIst(consent.signedAt)}</dd>
          ) : (
            <dd className="tc-print__rule" aria-hidden="true" />
          )}
        </div>
        <div className="tc-print__sig">
          <dt>Witness</dt>
          {consent.witnessName !== null ? (
            <dd>{consent.witnessName}</dd>
          ) : (
            <dd className="tc-print__rule" aria-hidden="true" />
          )}
        </div>
        <div className="tc-print__sig">
          <dt>Notes</dt>
          {consent.notes !== null ? (
            <dd>{consent.notes}</dd>
          ) : (
            <dd className="tc-print__rule" aria-hidden="true" />
          )}
        </div>
      </dl>
    </div>
  );
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; text: string }
  | {
      kind: 'ready';
      consent: TreatmentConsentView;
      template: ConsentTemplateWireView | null;
    };

function parsePositiveInt(raw: string | null): number | null {
  if (raw === null || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : null;
}

export default function TreatmentConsentPrintPage(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const caseSheetId = parsePositiveInt(params.get('caseSheetId'));
  const consentId = parsePositiveInt(params.get('consentId'));
  const appointmentId = parseConsultAppointmentId(params.get('appointmentId') ?? undefined);
  const backLabel = appointmentId === null ? 'Back to Home' : TREATMENT_CONSENT_PRINT_BACK;
  // Print opens a new tab: history may lead to about:blank or another page.
  // Accept only a visit ID; the existing consult route applies its read fence.
  const onBack = (): void => {
    void navigate(
      appointmentId === null ? '/dashboard/doctor/home' : consultPathForAppointment(appointmentId),
    );
  };
  const identity: PrintIdentity = {
    umr: params.get('umr'),
    name: params.get('name'),
    age: params.get('age'),
    sex: params.get('sex'),
    treatments: params.getAll('t'),
  };
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    if (caseSheetId === null || consentId === null) {
      setState({
        kind: 'error',
        text: 'This print link is incomplete. Open it from the consent panel.',
      });
      return;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    fetchCaseSheetConsents(caseSheetId)
      .then((wire) => {
        if (cancelled) return;
        const consent = wire.consents.find((c) => c.id === consentId) ?? null;
        if (!wire.available || consent === null) {
          setState({
            kind: 'error',
            text: 'This consent could not be found on the visit.',
          });
          return;
        }
        const template = wire.historicalTemplates?.[String(consent.id)] ?? null;
        if (!template || template.version !== consent.templateVersion || typeof template.authorisationText !== 'string' || !template.authorisationText.trim()) {
          setState({ kind: 'error', text: 'The recorded wording version is unavailable. Open the original signed file from the visit; today’s wording cannot replace it.' });
          return;
        }
        setState({ kind: 'ready', consent, template });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({ kind: 'error', text: consultErrorMessage(err).text });
      });
    return () => {
      cancelled = true;
    };
  }, [caseSheetId, consentId]);

  if (state.kind === 'loading') {
    return (
      <div className="dashboard">
        <LoadingState label="Preparing the consent form..." lines={4} />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div className="dashboard">
        <ErrorState
          title="The consent form could not be opened."
          detail={state.text}
          testId="treatment-consent-print-error"
          action={
            <AcButton size="sm" variant="secondary" onClick={onBack}>
              {backLabel}
            </AcButton>
          }
        />
      </div>
    );
  }
  return (
    <PrintSheet
      documentProvidesTitle={state.template !== null && isParthaForm(state.template)}
      title="Consent form"
      backLabel={backLabel}
      onBack={onBack}
      testId="treatment-consent-print"
    >
      <TreatmentConsentPrintContent
        consent={state.consent}
        template={state.template}
        identity={identity}
      />
    </PrintSheet>
  );
}
